// ── Spectator System ──
// Manages spectator camera modes, target cycling, and free-fly movement.
// Three modes:
//   1. FPP Follow: First-person through the eyes of a living player
//   2. TPP Follow: Third-person orbit around a living player
//   3. Free-fly: WASD + mouse noclip flight around the map

import * as THREE from 'three';
import { type FPSCamera } from '../renderer/camera';
import { type RemotePlayer } from '../gameplay/player';
import { type Connection } from '../net/connection';

export type SpectatorMode = 'fpp_follow' | 'tpp_follow' | 'free_fly';

const FREE_FLY_SPEED = 12; // m/s
const FREE_FLY_FAST_MULT = 3; // shift multiplier
const TPP_FOLLOW_DISTANCE = 4.0;
const TPP_FOLLOW_HEIGHT = 1.8;
const TPP_FOLLOW_SHOULDER = 0.6;

// Free-fly collision bounds
const FREE_FLY_FLOOR = 0.5;
const FREE_FLY_CEILING = 80;
const FREE_FLY_ARENA_HALF = 80;

export class SpectatorSystem {
  private _active = false;
  private _mode: SpectatorMode = 'fpp_follow';
  private _targetId = 0;

  // Free-fly state
  private flyPosition = new THREE.Vector3(0, 5, 0);
  private flyYaw = 0;
  private flyPitch = 0;

  // Smooth camera transition
  private transitionAlpha = 1; // 1 = fully transitioned

  // Keyboard state for free-fly (tracked internally to avoid conflicts)
  private keys = new Map<string, boolean>();
  private _onKeyDown: (e: KeyboardEvent) => void;
  private _onKeyUp: (e: KeyboardEvent) => void;

  // Guard against double listener removal
  private _listenersRemoved = false;

  constructor() {
    this._onKeyDown = (e: KeyboardEvent) => this.keys.set(e.code, true);
    this._onKeyUp = (e: KeyboardEvent) => this.keys.set(e.code, false);
  }

  // ── Public API ──

  get active(): boolean { return this._active; }
  get mode(): SpectatorMode { return this._mode; }
  get targetId(): number { return this._targetId; }

  /** Enter spectator mode, starting at the given target. */
  enter(initialTargetId: number, cameraPos?: THREE.Vector3): void {
    this._active = true;
    this._targetId = initialTargetId;
    this._mode = 'fpp_follow';
    this.transitionAlpha = 0;
    this._listenersRemoved = false;

    // Initialize free-fly position from current camera
    if (cameraPos) {
      this.flyPosition.copy(cameraPos);
    }

    // Listen for free-fly keys
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('keyup', this._onKeyUp);
  }

  /** Exit spectator mode (on respawn). */
  exit(): void {
    this._active = false;
    this._targetId = 0;
    this.keys.clear();
    if (!this._listenersRemoved) {
      this._listenersRemoved = true;
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('keyup', this._onKeyUp);
    }
  }

  /** Set spectate target (from server snapshot). */
  setTarget(targetId: number): void {
    if (targetId !== this._targetId) {
      this._targetId = targetId;
      this.transitionAlpha = 0;
    }
  }

  /** Cycle to next view mode: FPP follow -> TPP follow -> Free-fly -> FPP follow. */
  cycleMode(): void {
    switch (this._mode) {
      case 'fpp_follow':
        this._mode = 'tpp_follow';
        break;
      case 'tpp_follow':
        this._mode = 'free_fly';
        break;
      case 'free_fly':
        this._mode = 'fpp_follow';
        break;
    }
    this.transitionAlpha = 0;
  }

  /** Request next spectate target from server. */
  requestNext(connection: Connection): void {
    connection.send({ type: 'spectate_next', data: {} });
  }

  /** Request previous spectate target from server. */
  requestPrev(connection: Connection): void {
    connection.send({ type: 'spectate_prev', data: {} });
  }

  /**
   * Update spectator camera. Call each render frame.
   * @param camera The FPS camera to position
   * @param remotePlayers Map of remote players for follow modes
   * @param dt Frame delta time in seconds
   */
  update(
    camera: FPSCamera,
    remotePlayers: Map<number, RemotePlayer>,
    dt: number,
  ): void {
    if (!this._active) return;

    // Advance transition
    this.transitionAlpha = Math.min(1, this.transitionAlpha + dt * 6);

    if (this._mode === 'free_fly') {
      this.updateFreeFly(camera, dt);
    } else {
      this.updateFollow(camera, remotePlayers, dt);
    }
  }

  /** Dispose event listeners. */
  dispose(): void {
    this.keys.clear();
    if (!this._listenersRemoved) {
      this._listenersRemoved = true;
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('keyup', this._onKeyUp);
    }
  }

  // ── Internal ──

  private updateFollow(
    camera: FPSCamera,
    remotePlayers: Map<number, RemotePlayer>,
    _dt: number,
  ): void {
    const target = remotePlayers.get(this._targetId);
    if (!target) {
      // No valid target — fall back to free fly from current position
      this.flyPosition.copy(camera.camera.position);
      this.flyYaw = camera.getYaw();
      this.flyPitch = camera.getPitch();
      this._mode = 'free_fly';
      return;
    }

    const targetPos = target.position.clone();
    const targetYaw = target.yaw;
    const targetPitch = target.pitch;

    if (this._mode === 'fpp_follow') {
      // First-person through target's eyes
      const eyePos = targetPos.clone();
      eyePos.y += 1.6; // eye height

      camera.camera.position.copy(eyePos);

      // Build rotation from target's yaw + pitch
      const euler = new THREE.Euler(targetPitch, targetYaw, 0, 'YXZ');
      camera.camera.quaternion.setFromEuler(euler);

      // Sync free-fly state in case they switch
      this.flyPosition.copy(eyePos);
      this.flyYaw = targetYaw;
      this.flyPitch = targetPitch;
    } else {
      // Third-person follow (orbit behind target)
      const eyePos = targetPos.clone();
      eyePos.y += 1.6;

      const euler = new THREE.Euler(targetPitch, targetYaw, 0, 'YXZ');
      const forward = new THREE.Vector3(0, 0, -1).applyEuler(euler);
      const right = new THREE.Vector3(1, 0, 0).applyEuler(
        new THREE.Euler(0, targetYaw, 0, 'YXZ'),
      );

      const desiredPos = eyePos.clone();
      desiredPos.addScaledVector(forward, -TPP_FOLLOW_DISTANCE);
      desiredPos.y += TPP_FOLLOW_HEIGHT;
      desiredPos.addScaledVector(right, TPP_FOLLOW_SHOULDER);

      // Smooth follow
      camera.camera.position.lerp(desiredPos, 0.12);
      camera.camera.lookAt(eyePos.x, eyePos.y - 0.2, eyePos.z);

      // Sync free-fly state
      this.flyPosition.copy(camera.camera.position);
      this.flyYaw = targetYaw;
      this.flyPitch = targetPitch;
    }
  }

  private updateFreeFly(camera: FPSCamera, dt: number): void {
    // Read mouse look from the camera's internal yaw/pitch
    this.flyYaw = camera.getYaw();
    this.flyPitch = camera.getPitch();

    // WASD movement
    let moveX = 0;
    let moveY = 0;
    let moveZ = 0;

    if (this.keys.get('KeyW')) moveZ -= 1;
    if (this.keys.get('KeyS')) moveZ += 1;
    if (this.keys.get('KeyA')) moveX -= 1;
    if (this.keys.get('KeyD')) moveX += 1;
    if (this.keys.get('Space')) moveY += 1;
    if (this.keys.get('ControlLeft') || this.keys.get('ControlRight')) moveY -= 1;

    // Normalize horizontal
    const hLen = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (hLen > 0) {
      moveX /= hLen;
      moveZ /= hLen;
    }

    // Speed multiplier
    const fast = this.keys.get('ShiftLeft') || this.keys.get('ShiftRight');
    const speed = FREE_FLY_SPEED * (fast ? FREE_FLY_FAST_MULT : 1) * dt;

    // Transform to world space (yaw only, so vertical stays true)
    const sinYaw = Math.sin(this.flyYaw);
    const cosYaw = Math.cos(this.flyYaw);

    // Forward is along the full look direction (including pitch) for free fly
    const cosPitch = Math.cos(this.flyPitch);
    const sinPitch = Math.sin(this.flyPitch);

    // Forward direction (respects pitch for flying up/down)
    const fwdX = -sinYaw * cosPitch;
    const fwdY = sinPitch;
    const fwdZ = -cosYaw * cosPitch;

    // Right direction (horizontal only)
    const rightX = cosYaw;
    const rightZ = -sinYaw;

    this.flyPosition.x += (fwdX * -moveZ + rightX * moveX) * speed;
    this.flyPosition.y += (fwdY * -moveZ + moveY) * speed;
    this.flyPosition.z += (fwdZ * -moveZ + rightZ * moveX) * speed;

    // Clamp to arena bounds
    this.flyPosition.y = Math.max(this.flyPosition.y, FREE_FLY_FLOOR);
    this.flyPosition.y = Math.min(this.flyPosition.y, FREE_FLY_CEILING);
    this.flyPosition.x = Math.max(-FREE_FLY_ARENA_HALF, Math.min(FREE_FLY_ARENA_HALF, this.flyPosition.x));
    this.flyPosition.z = Math.max(-FREE_FLY_ARENA_HALF, Math.min(FREE_FLY_ARENA_HALF, this.flyPosition.z));

    // Apply to camera
    camera.camera.position.copy(this.flyPosition);
    const euler = new THREE.Euler(this.flyPitch, this.flyYaw, 0, 'YXZ');
    camera.camera.quaternion.setFromEuler(euler);
  }
}
